import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const ContractPublicView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${apiUrl}/contracts/public/${token}`);
        if (res.data.success) {
          setData(res.data.data);
          if (res.data.data.status === 'accepted') setDone(true);
        } else {
          setError(res.data.message || 'Contract not found.');
        }
      } catch (err) {
        const msg =
          err.response?.status === 403
            ? 'This service is not available.'
            : err.response?.data?.message || 'Contract not found or the link is invalid.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!agreed || !signerName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${apiUrl}/contracts/public/${token}/accept`, {
        signerName: signerName.trim(),
        agreed: true,
      });
      if (res.data.success) {
        setDone(true);
        if (res.data.data) {
          setData((prev) => ({
            ...prev,
            status: res.data.data.status,
            accepted_at: res.data.data.accepted_at,
            signer_name: res.data.data.signer_name,
            pdf_available:
              res.data.data.pdf_available !== undefined
                ? res.data.data.pdf_available
                : prev?.pdf_available,
          }));
        }
      } else {
        setError(res.data.message || 'Could not record acceptance.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not record acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  const pdfHref = data?.pdf_available ? `${apiUrl}/contracts/public/${token}/pdf` : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white shadow rounded-lg p-8 max-w-md text-center text-gray-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-4 flex flex-wrap gap-3 items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Rental contract</h1>
              {data?.quote_number && (
                <p className="text-sm text-gray-600">Quote {data.quote_number}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {pdfHref && (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900"
                >
                  Download contract
                </a>
              )}
            </div>
          </div>

          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 text-red-800 text-sm rounded-md">{error}</div>
          )}

          <div
            className="contract-html-preview px-6 py-4 border-b border-gray-100 max-h-[60vh] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: data?.html_body || '' }}
          />

          {done || data?.status === 'accepted' ? (
            <div className="px-6 py-6 bg-green-50 border-t border-green-100">
              <p className="text-green-900 font-medium">Thank you. This contract has been accepted.</p>
              {data?.signer_name && (
                <p className="text-sm text-green-800 mt-1">Signed as: {data.signer_name}</p>
              )}
              {data?.accepted_at && (
                <p className="text-xs text-green-700 mt-2">
                  {new Date(data.accepted_at).toLocaleString()}
                </p>
              )}
              {pdfHref ? (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-4 px-4 py-2 bg-green-800 text-white text-sm font-medium rounded-md hover:bg-green-900"
                >
                  Download contract copy
                </a>
              ) : (
                <p className="mt-4 text-sm text-green-800">
                  A downloadable copy is not available from this link. Contact the rental company if you need a file.
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={handleAccept} className="px-6 py-6 space-y-4 bg-gray-50">
              <p className="text-sm text-gray-700">
                By signing below, you agree to the terms shown in this contract and the line items from your quote.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-gray-800">I have read this contract and agree to its terms.</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full legal name</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Your full name"
                  required
                  minLength={2}
                />
              </div>
              <button
                type="submit"
                disabled={submitting || !agreed || signerName.trim().length < 2}
                className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-md font-medium disabled:opacity-50 hover:bg-blue-700"
              >
                {submitting ? 'Submitting…' : 'Accept contract'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractPublicView;
